/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReportContext } from '../contexts';
import { Filter, RoundHistory } from '../../config/interfaces';
import { reportsApi } from '../apis/Reports.api';

/**
 * Redux slice for managing report-related state using Redux Toolkit's `createSlice`.
 *
 * This slice handles actions related to searching, fetching, filtering, and managing various reports,
 * including patient history, round history, compliance, census, employee rounds, and frequency reports.
 * It also manages pagination, sorting, searching, and filter state for these reports.
 *
 * @property {object} initialState - The initial state for the report context.
 * @property {Function} clearPatientSearch - Clears patient search results.
 * @property {Function} clearPatientRoundHistory - Clears patient round history.
 * @property {Function} clearEmployeeRoundHistory - Clears employee round history.
 * @property {Function} setComplianceReportFilter - Sets the filter for compliance report.
 * @property {Function} clearComplianceReportFilter - Clears the filter for compliance report.
 * @property {Function} setpatientSummaryReportFilter - Sets the filter for compliance report from summary page.
 * @property {Function} clearpatientSummaryReportFilter - Clears the filter for compliance report.
 * @property {Function} setEmployeeSummaryReportFilter - Sets the filter for employee summary report.
 * @property {Function} clearEmployeeSummaryReportFilter - Clears the filter for employee summary report.
 * @property {Function} clearPatientRoundsReport - Clears patient rounds report data.
 * @property {Function} setPatientRoundsReportPage - Sets the current page for patient rounds report pagination.
 * @property {Function} setPatientRoundsReportSearchQuery - Sets the search query for patient rounds report.
 * @property {Function} setPatientRoundsReportSort - Sets the sort key and direction for patient rounds report.
 * @property {Function} clearCensusReport - Clears census report data.
 * @property {Function} setCensusReportPage - Sets the current page for census report pagination.
 * @property {Function} setCensusReportSearchQuery - Sets the search query for census report.
 * @property {Function} setCensusReportSort - Sets the sort key and direction for census report.
 * @property {Function} clearEmployeeRoundsReport - Clears employee rounds report data.
 * @property {Function} setEmployeeRoundsReportPage - Sets the current page for employee rounds report pagination.
 * @property {Function} setEmployeeRoundsReportSearchQuery - Sets the search query for employee rounds report.
 * @property {Function} setEmployeeRoundsReportSort - Sets the sort key and direction for employee rounds report.
 * @property {Function} clearEmployeeFreqReport - Clears employee frequency report data.
 * @property {Function} setEmployeeFreqReportPage - Sets the current page for employee frequency report pagination.
 * @property {Function} setEmployeeFreqReportSearchQuery - Sets the search query for employee frequency report.
 * @property {Function} setEmployeeFreqReportSort - Sets the sort key and direction for employee frequency report.
 * @property {Function} setRoundsReportFilter - Sets the filter for rounds reports and resets pagination.
 * @property {Function} clearRoundsReportFilter - Clears the rounds report filter and resets pagination.
 * @property {Function} setCensusReportFilter - Sets the filter for census report.
 * @property {Function} clearCensusReportFilter - Clears the census report filter.
 * @property {Function} setFrequencyReportFilter - Sets the filter for frequency report.
 * @property {Function} clearFrequencyReportFilter - Clears the frequency report filter.
 *
 * @example
 * dispatch(searchPatientHistory({ facilityId: '12345', patientSearch: 'Smith' }));
 * dispatch(getPatientRoundsReport({ facilityId: '12345', unitId: ['A.3N'], page: 1, ... }));
 * dispatch(setEmployeeRoundsReportSort({ sortKey: 'created', sortDir: 'desc' }));
 * dispatch(clearRoundsReportFilter());
 *
 * @returns {object} The report slice reducer and actions.
 */
const reportSlice = createSlice({
  name: 'report',
  initialState: ReportContext,
  reducers: {
    clearPatientSearch: state => {
      return {
        ...state,
        searchedPatients: []
      };
    },
    getPatientRoundHistorySuccess: (
      state,
      action: PayloadAction<RoundHistory[]>
    ) => {
      return {
        ...state,
        patientRoundHistory: action.payload
      };
    },
    clearPatientRoundHistory: state => {
      return {
        ...state,
        patientRoundHistory: []
      };
    },
    clearEmployeeRoundHistory: state => {
      return {
        ...state,
        employeeRoundHistory: []
      };
    },
    setComplianceReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        complianceReportFilter: action.payload
      };
    },
    clearComplianceReportFilter: state => {
      return {
        ...state,
        complianceReportFilter: {} as Filter
      };
    },
    setpatientSummaryReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        patientSummaryReportFilter: action.payload
      };
    },
    clearpatientSummaryReportFilter: state => {
      return {
        ...state,
        patientSummaryReportFilter: {} as Filter
      };
    },
    setEmployeeSummaryReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        employeeSummaryReportFilter: action.payload
      };
    },
    clearEmployeeSummaryReportFilter: state => {
      return {
        ...state,
        employeeSummaryReportFilter: {} as Filter
      };
    },
    clearPatientRoundsReport: state => {
      return {
        ...state,
        patientRoundsReport: [],
        totalTableCount: 0
      };
    },
    setPatientRoundsReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentPatientRoundsReportPage: action.payload
      };
    },
    setPatientRoundsReportSearchQuery: (
      state,
      action: PayloadAction<string>
    ) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentPatientRoundsReportPage: 1
      };
    },
    setPatientRoundsReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortRoundsKey: action.payload.sortKey,
          sortRoundsDir: action.payload.sortDir
        }
      };
    },
    clearCensusReport: state => {
      return {
        ...state,
        censusReport: [],
        totalTableCount: 0
      };
    },
    setCensusReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentCensusReportPage: action.payload
      };
    },
    setCensusReportSearchQuery: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentCensusReportPage: 1
      };
    },
    setCensusReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortCensusKey: action.payload.sortKey,
          sortRoundsDir: action.payload.sortDir
        }
      };
    },
    clearEmployeeRoundsReport: state => {
      return {
        ...state,
        employeeRoundsReport: []
      };
    },
    setEmployeeRoundsReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentEmployeeRoundsReportPage: action.payload
      };
    },
    setEmployeeRoundsReportSearchQuery: (
      state,
      action: PayloadAction<string>
    ) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentEmployeeRoundsReportPage: 1,
        currentEmployeeFreqReportPage: 1
      };
    },
    setEmployeeRoundsReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortRoundsKey: action.payload.sortKey,
          sortRoundsDir: action.payload.sortDir
        }
      };
    },
    clearEmployeeFreqReport: state => {
      return {
        ...state,
        employeeFreqReport: []
      };
    },
    setEmployeeFreqReportPage: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        currentEmployeeFreqReportPage: action.payload
      };
    },
    setEmployeeFreqReportSearchQuery: (
      state,
      action: PayloadAction<string>
    ) => {
      return {
        ...state,
        reportSearchQuery: action.payload,
        currentEmployeeFreqReportPage: 1
      };
    },
    setEmployeeFreqReportSort: (
      state,
      action: PayloadAction<{ sortKey: string; sortDir: string }>
    ) => {
      return {
        ...state,
        ...{
          sortFreqKey: action.payload.sortKey,
          sortFreqDir: action.payload.sortDir
        }
      };
    },
    setRoundsReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        roundsReportFilter: action.payload,
        currentPatientRoundsReportPage: 1,
        currentEmployeeRoundsReportPage: 1,
        currentCensusReportPage: 1
      };
    },
    clearRoundsReportFilter: state => {
      return {
        ...state,
        roundsReportFilter: {} as Filter,
        currentPatientRoundsReportPage: 1,
        currentEmployeeRoundsReportPage: 1,
        currentCensusReportPage: 1
      };
    },
    setCensusReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        censusReportFilter: action.payload,
        currentCensusReportPage: 1
      };
    },
    clearCensusReportFilter: state => {
      return {
        ...state,
        censusReportFilter: {} as Filter,
        currentCensusReportPage: 1
      };
    },
    setFrequencyReportFilter: (state, action: PayloadAction<Filter>) => {
      return {
        ...state,
        frequencyReportFilter: action.payload,
        currentEmployeeFreqReportPage: 1
      };
    },
    clearFrequencyReportFilter: state => {
      return {
        ...state,
        frequencyReportFilter: {} as Filter,
        currentEmployeeFreqReportPage: 1
      };
    }
  },
  extraReducers: builder => {
    builder.addMatcher(
      reportsApi.endpoints.getPatientRoundHistory.matchFulfilled,
      (state, action) => ({
        ...state,
        patientRoundHistory: action.payload
      })
    );
    builder.addMatcher(
      reportsApi.endpoints.getPatientRoundHistory.matchRejected,
      (state, action) => ({
        ...state,
        patientRoundHistory: []
      })
    );
    builder.addMatcher(
      reportsApi.endpoints.getEmployeeRoundHistory.matchFulfilled,
      (state, action) => ({
        ...state,
        employeeRoundHistory: action.payload
      })
    );
    builder.addMatcher(
      reportsApi.endpoints.getEmployeeRoundHistory.matchRejected,
      (state, action) => ({
        ...state,
        employeeRoundHistory: []
      })
    );
  }
});

export const {
  clearPatientSearch,
  getPatientRoundHistorySuccess,
  clearPatientRoundHistory,
  clearEmployeeRoundHistory,
  setComplianceReportFilter,
  clearComplianceReportFilter,
  setpatientSummaryReportFilter,
  clearpatientSummaryReportFilter,
  setEmployeeSummaryReportFilter,
  clearEmployeeSummaryReportFilter,
  clearPatientRoundsReport,
  setPatientRoundsReportPage,
  setPatientRoundsReportSearchQuery,
  setPatientRoundsReportSort,
  clearCensusReport,
  setCensusReportPage,
  setCensusReportSearchQuery,
  setCensusReportSort,
  clearEmployeeRoundsReport,
  setEmployeeRoundsReportPage,
  setEmployeeRoundsReportSearchQuery,
  setEmployeeRoundsReportSort,
  clearEmployeeFreqReport,
  setEmployeeFreqReportPage,
  setEmployeeFreqReportSearchQuery,
  setEmployeeFreqReportSort,
  setRoundsReportFilter,
  clearRoundsReportFilter,
  setCensusReportFilter,
  clearCensusReportFilter,
  setFrequencyReportFilter,
  clearFrequencyReportFilter
} = reportSlice.actions;

export default reportSlice.reducer;
